import { DndContext, closestCenter, type DragEndEvent } from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import cn from "@/v2-ui/utils/utils.cn"
import Placeholder from "@/v2-ui/Placeholder"
import { DotText } from "@/v2-backoffice-client/components/dot"
import LayerOrganizeSortableRow from "@/v2-backoffice-client/layer/organize/LayerOrganizeSortableRow"
import { useLayerOrganizeDragSensors } from "@/v2-backoffice-client/layer/organize/layer.organize.dnd"
import { UNCATEGORIZED_CODE } from "@/v2-backoffice-client/layer/organize/layer.organize.constants"
import { moveWithinSection } from "@/v2-backoffice-client/layer/organize/layer.organize.utils"
import type { LayerCategoryFragment } from "@/v2-backoffice-client/layer/category/__types__/layer.category.fragment"
import type { LayerFragment } from "@/v2-backoffice-client/layer/__types__/layer.fragment"

export type LayerOrganizeCategoriesTabProps = {
  /** Every layer code, ascending Priority (= the chooser's top-to-bottom). */
  globalOrder: string[]
  /** The category codes, ascending Priority, without the pseudo-category. */
  categoryOrder: string[]
  selectedCategory: string | null
  layersByCode: Map<string, LayerFragment>
  categoriesByCode: Map<string, LayerCategoryFragment>
  onGlobalOrderChange(order: string[]): void
  onCategoryOrderChange(order: string[]): void
  onSelectedCategoryChange(code: string): void
}

/**
 * The "Categories" tab of the organizer, mirroring the app's Add-layers
 * chooser. Left column: the chooser's sections (categories), draggable to
 * reorder. Right column: the selected section's layers, draggable to
 * reorder within the section — which reorders them in the ONE global
 * ranking, so the move also shows up in the draw order.
 *
 * Owns the drag handling for both columns; the pending orders themselves
 * live in the dialog because they are shared with the draw-order tab and
 * committed by the dialog's single Save button.
 */
function LayerOrganizeCategoriesTab(props: LayerOrganizeCategoriesTabProps) {
  const {
    globalOrder,
    categoryOrder,
    selectedCategory,
    layersByCode,
    categoriesByCode,
    onGlobalOrderChange,
    onCategoryOrderChange,
    onSelectedCategoryChange,
  } = props

  const sensors = useLayerOrganizeDragSensors()

  // The selected category's layers, in chooser order (= global order).
  const sectionCodes = selectedCategory
    ? globalOrder.filter((code) =>
        layersByCode.get(code)?.CategoryCodes.includes(selectedCategory),
      )
    : []

  function countLayersIn(categoryCode: string): number {
    let count = 0
    for (const code of globalOrder) {
      if (layersByCode.get(code)?.CategoryCodes.includes(categoryCode)) {
        count += 1
      }
    }
    return count
  }

  function onCategoryDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const from = categoryOrder.indexOf(String(active.id))
    const to = categoryOrder.indexOf(String(over.id))
    if (from < 0 || to < 0) return
    onCategoryOrderChange(arrayMove(categoryOrder, from, to))
  }

  function onSectionDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const from = sectionCodes.indexOf(String(active.id))
    const to = sectionCodes.indexOf(String(over.id))
    onGlobalOrderChange(moveWithinSection(globalOrder, sectionCodes, from, to))
  }

  return (
    <>
      <p className="pb-2 text-xs text-muted-foreground">
        Left: the chooser&apos;s sections, top-to-bottom — drag to reorder.
        Right: the selected section&apos;s layers — drag to reorder within it.
        Layers share ONE ranking across the whole catalog, so moving a layer
        here also moves it in the draw order and in its other categories.
      </p>
      <div className="grid max-h-[55vh] grid-cols-[minmax(220px,1fr)_2fr] gap-3">
        <div className="flex flex-col gap-1 overflow-y-auto pr-1">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onCategoryDragEnd}
          >
            <SortableContext
              items={categoryOrder}
              strategy={verticalListSortingStrategy}
            >
              <div role="list" className="flex flex-col gap-1">
                {categoryOrder.map((code, index) => {
                  const category = categoriesByCode.get(code)
                  if (!category) return null
                  return (
                    <div
                      key={code}
                      role="listitem"
                      data-feature-code="UI_BackofficeLayers_Organize_SelectCategory"
                      onClick={() => onSelectedCategoryChange(code)}
                    >
                      <LayerOrganizeSortableRow
                        id={code}
                        className={cn(
                          selectedCategory === code && "border-primary",
                        )}
                      >
                        <span className="w-5 shrink-0 tabular-nums text-muted-foreground">
                          {index + 1}
                        </span>
                        <span className="min-w-0 flex-1 truncate">
                          {category.Name}
                        </span>
                        {!category.IsEnabled && (
                          <DotText variant="destructive">off</DotText>
                        )}
                        <span className="shrink-0 text-muted-foreground">
                          {countLayersIn(code)}
                        </span>
                      </LayerOrganizeSortableRow>
                    </div>
                  )
                })}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        <div className="flex flex-col gap-1 overflow-y-auto pr-1">
          {sectionCodes.length === 0 ? (
            <Placeholder className="h-32">
              No layers in this category.
            </Placeholder>
          ) : (
            <>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Shown first in the chooser
              </p>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onSectionDragEnd}
              >
                <SortableContext
                  items={sectionCodes}
                  strategy={verticalListSortingStrategy}
                >
                  <div role="list" className="flex flex-col gap-1">
                    {sectionCodes.map((code, index) => {
                      const layer = layersByCode.get(code)
                      if (!layer) return null
                      const otherCategories = layer.CategoryCodes.filter(
                        (categoryCode) =>
                          categoryCode !== selectedCategory &&
                          categoryCode !== UNCATEGORIZED_CODE,
                      )
                      return (
                        <div key={code} role="listitem">
                          <LayerOrganizeSortableRow id={code}>
                            <span className="w-6 shrink-0 tabular-nums text-muted-foreground">
                              {index + 1}
                            </span>
                            <span className="min-w-0 flex-1 truncate">
                              {layer.Name}
                              <span className="ml-2 text-muted-foreground">
                                {layer.FeatureCode}
                              </span>
                            </span>
                            {otherCategories.length > 0 && (
                              <span
                                className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                                title={`Also in: ${otherCategories
                                  .map(
                                    (categoryCode) =>
                                      categoriesByCode.get(categoryCode)
                                        ?.Name ?? categoryCode,
                                  )
                                  .join(", ")}`}
                              >
                                +{otherCategories.length}
                              </span>
                            )}
                          </LayerOrganizeSortableRow>
                        </div>
                      )
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default LayerOrganizeCategoriesTab
